/**
 * CohortForm State Management using constate
 *
 * This module provides centralized state management for the CohortForm feature
 * using the constate library pattern (similar to EvidenceState.tsx).
 *
 * ## Pattern Overview
 *
 * 1. **Main State Hook** (`useCohortFormState`): Contains all form state and actions
 * 2. **Constate Provider** (`CohortFormProvider`): Wraps components needing state
 * 3. **Selector Hooks**: Granular hooks for specific state slices to prevent re-renders
 *
 * ## Available Selector Hooks
 *
 * - `useCohortNavigation`: Step navigation (currentStepId, goNext, goBack)
 * - `useCohortPatientData`: Patient list, pagination, filtering
 * - `useCohortForm`: Form instance and filling helpers
 * - `useCohortFormErrors`: Error state management
 * - `useCohortLinkedCohorts`: Linked cohort selection
 * - `useCohortExcludedPatients`: Patient exclusion state
 * - `useCohortUIState`: UI flags (loading, modals, filters)
 * - `useCohortContext`: URL state (messageId, clinicalTrial)
 *
 * ## Usage Example
 *
 * ```tsx
 * // In parent component:
 * <CohortFormProvider>
 *   <CohortFormSteps />
 * </CohortFormProvider>
 *
 * // In child component:
 * function PatientStep() {
 *   const { patientsData, loadingPatients } = useCohortPatientData();
 *   const { goNext } = useCohortNavigation();
 *   // ...
 * }
 * ```
 *
 * ## Benefits
 *
 * - Automatic selector-based subscriptions (prevents unnecessary re-renders)
 * - Clean separation of concerns
 * - Easy to extend with new selectors
 * - Eliminates prop drilling
 */
import type { AdvancedFilterQuery, Cohort, PatientSearchFilters } from '@packages/types';
import { useGetConversationByMessageIdQuery } from '@ui/features/Chat/services/api';
import { useGetClinicalTrialJobQuery } from '@ui/features/ClinicalTrial/services/api';
import { useCohortClinicalTrialJobId } from '@ui/features/Cohort/hooks/useCohortClinicalTrialJobId';
import { useCohortMessageId } from '@ui/features/Cohort/hooks/useCohortMessageId';
import { useGetPatientListQuery } from '@ui/features/Patient/services/api';
import { Form, notification } from 'antd';
import constate from 'constate';
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { logger } from '@ui/utils/logger';
import { DefaultPagination, STEP_IDS } from '../shared';
import type { CohortFormValues, IPagination, IPatient, StepId } from '../types';

/**
 * Main state hook for CohortForm.
 * Contains all form state, patient data queries, and navigation logic.
 *
 * @internal This hook is consumed by constate and should not be used directly.
 * Use the exported selector hooks instead.
 */
function useCohortFormState() {
    const [form] = Form.useForm<CohortFormValues>();
    const location = useLocation();

    // =========================================================================
    // Navigation State
    // =========================================================================
    const [currentStepId, setCurrentStepId] = useState<StepId>(STEP_IDS.CRITERIA_SELECTION);

    // =========================================================================
    // Patient Data State
    // =========================================================================
    const [pagination, setPagination] = useState<IPagination>(DefaultPagination);
    const [filter, setFilter] = useState<string | undefined>(undefined);
    const [patientSearchTerm, setPatientSearchTerm] = useState<string>('');
    const [patientOrderBy, setPatientOrderBy] = useState<string>('');
    const [patientOrderDirection, setPatientOrderDirection] = useState<'asc' | 'desc' | ''>('');

    // =========================================================================
    // Form State
    // =========================================================================
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [selectedLinkedCohorts, setSelectedLinkedCohorts] = useState<Cohort[]>([]);
    const [excludedPatients, setExcludedPatients] = useState<IPatient[]>([]);
    const [filteredTotalCount, setFilteredTotalCount] = useState<number>(0);

    // =========================================================================
    // UI State
    // =========================================================================
    const [showFilter, setShowFilter] = useState(false);
    const [isFormLoading, setIsFormLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [createdCohortName, setCreatedCohortName] = useState('');
    const [pendingFieldError, setPendingFieldError] = useState<{ field: string; message: string } | null>(null);

    // Store filter when leaving step 1, restore when returning
    const savedFilterRef = useRef<AdvancedFilterQuery | undefined>(undefined);

    // =========================================================================
    // URL-Synced State (via nuqs hooks)
    // =========================================================================
    const { messageId } = useCohortMessageId({
        initialCohortMessageId: location.state?.initialCohort?.message_id,
    });

    const { clinicalTrialJobId, hasClinicalTrialJobId } = useCohortClinicalTrialJobId();

    // Fetch clinical trial job data when clinicalTrialJobId is present
    const { data: clinicalTrialJobData } = useGetClinicalTrialJobQuery(clinicalTrialJobId!, {
        skip: !clinicalTrialJobId,
    });

    // =========================================================================
    // Clinical Trial Description
    // =========================================================================
    const clinicalTrialDescription = useMemo(() => {
        // First check job data from URL param
        if (clinicalTrialJobData) {
            const { nct_id, trial_title } = clinicalTrialJobData;
            if (nct_id) {
                const truncatedTitle = trial_title
                    ? `${trial_title.substring(0, 60)}${trial_title.length > 60 ? '...' : ''}`
                    : '';
                return truncatedTitle ? `${nct_id} - ${truncatedTitle}` : nct_id;
            }
            const latestExecution = clinicalTrialJobData.executions?.[0];
            if (latestExecution?.final_patient_count !== undefined) {
                return `Clinical Trial Criteria (${latestExecution.final_patient_count} patients)`;
            }
            return clinicalTrialJobData.name || 'Clinical Trial Criteria';
        }

        // Fallback to navigation state (legacy)
        const clinicalTrialData = location.state?.clinicalTrialData;
        if (!clinicalTrialData) return null;

        if (clinicalTrialData.trialMetadata) {
            const { nct_id, title } = clinicalTrialData.trialMetadata;
            if (nct_id) {
                const truncatedTitle = title ? `${title.substring(0, 60)}${title.length > 60 ? '...' : ''}` : '';
                return truncatedTitle ? `${nct_id} - ${truncatedTitle}` : nct_id;
            }
        }

        if (clinicalTrialData.executionSummary?.final_patient_count !== undefined) {
            return `Clinical Trial Criteria (${clinicalTrialData.executionSummary.final_patient_count} patients)`;
        }

        return 'Clinical Trial Criteria';
    }, [clinicalTrialJobData, location.state?.clinicalTrialData]);

    // =========================================================================
    // Conversation Data (for chat_filter)
    // =========================================================================
    const { data: conversationData } = useGetConversationByMessageIdQuery(
        { messageId: messageId! },
        { skip: !messageId }
    );

    // =========================================================================
    // Patient Query
    // =========================================================================
    const watchedPatientFilter =
        Form.useWatch('patientAdvancedFilter', form) || form.getFieldValue('patientAdvancedFilter');
    const watchedIncludeElasticsearchForFilters = Form.useWatch('includeElasticsearchForFilters', form);

    // Watch random selection values to update patient list when sample size changes
    const randomEnabled = Form.useWatch('randomEnabled', form);
    const randomCount = Form.useWatch('randomCount', form);
    const randomSeedStr = Form.useWatch('randomSeed', form);

    const queryParams: PatientSearchFilters = useMemo(() => {
        const includeElasticsearchForFilters =
            watchedIncludeElasticsearchForFilters ?? form.getFieldValue('includeElasticsearchForFilters');

        const params: PatientSearchFilters = {
            filters: watchedPatientFilter || undefined,
            pageSize: pagination.pageSize,
            page: pagination.current,
            search: patientSearchTerm || undefined,
            orderBy: patientOrderBy || undefined,
            orderDirection: patientOrderBy ? patientOrderDirection || undefined : undefined,
            messageId: messageId || undefined,
            clinicalTrialJobId: clinicalTrialJobId || undefined,
            includeDocuments: true,
            includeElasticsearchForFilters: includeElasticsearchForFilters || undefined,
        };

        // Add random selection parameters if enabled
        if (randomEnabled && typeof randomCount === 'number' && randomCount > 0) {
            params.randomSampleSize = randomCount;
            if (randomSeedStr && randomSeedStr.trim() !== '') {
                const parsedSeed = parseInt(randomSeedStr, 10);
                if (!isNaN(parsedSeed)) {
                    params.randomSeed = parsedSeed;
                }
            }
        }

        return params;
    }, [
        watchedPatientFilter,
        watchedIncludeElasticsearchForFilters,
        pagination,
        patientSearchTerm,
        patientOrderBy,
        patientOrderDirection,
        messageId,
        randomEnabled,
        randomCount,
        randomSeedStr,
        clinicalTrialJobId,
        form,
    ]);

    // Skip provider patient list when on Exclude step to avoid double fetch (step has its own usePatientTableFilters with its pagination)
    const { data: patientsData, isFetching: loadingPatients } = useGetPatientListQuery(queryParams, {
        skip: currentStepId === STEP_IDS.PATIENT_MANAGEMENT,
    });

    const { data: unfilteredPatientsData } = useGetPatientListQuery({
        pageSize: 1,
        page: 1,
        messageId: messageId || undefined,
        clinicalTrialJobId: clinicalTrialJobId || undefined,
    });

    const paginationWithTotal = useMemo(
        () => ({
            ...pagination,
            total: patientsData?.total ?? pagination.total,
        }),
        [pagination, patientsData?.total]
    );

    // =========================================================================
    // Navigation Callbacks
    // =========================================================================
    const goNext = useCallback(() => {
        const patientFilter = form.getFieldValue('patientAdvancedFilter');
        const totalPatients = (patientsData?.total as number) || 0;

        if (currentStepId === STEP_IDS.CRITERIA_SELECTION) {
            if (totalPatients === 0) {
                notification.warning({
                    message: 'No Patients Found',
                    description: 'No patients match the current criteria. Please adjust your filters or selection.',
                    placement: 'topRight',
                    duration: 4,
                });
                return;
            }

            if (patientFilter) {
                savedFilterRef.current = patientFilter;
            }
        }

        const stepOrder: StepId[] = [
            STEP_IDS.CRITERIA_SELECTION,
            STEP_IDS.PATIENT_MANAGEMENT,
            STEP_IDS.COHORT_DETAILS,
            STEP_IDS.REVIEW,
        ];

        const currentIndex = stepOrder.indexOf(currentStepId);
        if (currentIndex < stepOrder.length - 1) {
            setCurrentStepId(stepOrder[currentIndex + 1]);
        }
    }, [currentStepId, form, patientsData?.total]);

    const goBack = useCallback(() => {
        const stepOrder: StepId[] = [
            STEP_IDS.CRITERIA_SELECTION,
            STEP_IDS.PATIENT_MANAGEMENT,
            STEP_IDS.COHORT_DETAILS,
            STEP_IDS.REVIEW,
        ];

        const currentIndex = stepOrder.indexOf(currentStepId);
        if (currentIndex > 0) {
            setCurrentStepId(stepOrder[currentIndex - 1]);
        }
    }, [currentStepId]);

    // =========================================================================
    // Form Helpers
    // =========================================================================
    const clearError = useCallback((field: string) => {
        setErrors(prev => {
            const copy = { ...prev };
            delete copy[field];
            return copy;
        });
    }, []);

    const handleSearch = useCallback((search: string) => {
        setPatientSearchTerm(search);
        setPagination(prev => ({ ...prev, current: 1 }));
    }, []);

    const handleOrderChange = useCallback((orderBy: string, orderDirection: 'asc' | 'desc') => {
        setPatientOrderBy(orderBy);
        setPatientOrderDirection(orderDirection);
        setFilter(`${orderBy}/${orderDirection}`);
        setPagination(prev => ({ ...prev, current: 1 }));
    }, []);

    const handleFillingForm = useCallback(
        (selectedCohort: Cohort) => {
            setIsFormLoading(true);

            let parsedFilter: { groups: any[] } | undefined = undefined;
            if (selectedCohort.filter_criteria) {
                try {
                    const filterCriteria =
                        typeof selectedCohort.filter_criteria === 'string'
                            ? JSON.parse(selectedCohort.filter_criteria)
                            : selectedCohort.filter_criteria;

                    // Support both old format: {mode: "standard", filter: {groups: [...], excludedPatientIds: [...]}}
                    // and new format: {groups: [...], excludedPatientIds: [...]}
                    let groups = null;

                    if (filterCriteria) {
                        // Check for old format (nested filter object)
                        if (filterCriteria.filter && filterCriteria.filter.groups) {
                            groups = filterCriteria.filter.groups;
                        }
                        // Check for new format (direct groups)
                        else if (filterCriteria.groups) {
                            groups = filterCriteria.groups;
                        }

                        if (groups && groups.length > 0) {
                            // Create filter object with groups only (exclude excludedPatientIds)
                            parsedFilter = { groups };
                        }
                    }
                    setShowFilter(false);
                } catch (error) {
                    logger.error('Failed to parse filter_criteria', error);
                }
            }

            form.setFieldsValue({
                name: selectedCohort.name || '',
                description: selectedCohort.description || '',
                category_mode: selectedCohort.category_id ? 'existing' : 'new',
                category_id: selectedCohort.category_id || undefined,
                new_category_name: selectedCohort.category_id ? '' : 'My Cases',
                patientAdvancedFilter: parsedFilter,
                linked_cohort_ids: [selectedCohort.cohort_id],
                chat_filter: (selectedCohort as any)?.chat_filter || undefined,
                sql_query: (selectedCohort as any)?.sql_query || undefined,
            });

            setSelectedLinkedCohorts([selectedCohort]);
            setIsFormLoading(false);
        },
        [form]
    );

    // =========================================================================
    // Effects
    // =========================================================================

    // Restore filter when returning to CRITERIA_SELECTION step
    useEffect(() => {
        if (currentStepId === STEP_IDS.CRITERIA_SELECTION && savedFilterRef.current) {
            const currentFilter = form.getFieldValue('patientAdvancedFilter');
            if (!currentFilter) {
                form.setFieldValue('patientAdvancedFilter', savedFilterRef.current);
            }
        }
    }, [currentStepId, form]);

    // Set field error when COHORT_DETAILS step is rendered
    useEffect(() => {
        if (currentStepId === STEP_IDS.COHORT_DETAILS && pendingFieldError) {
            const timer = setTimeout(() => {
                form.setFields([
                    {
                        name: pendingFieldError.field as keyof CohortFormValues,
                        errors: [pendingFieldError.message],
                    },
                ]);
                setPendingFieldError(null);
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [currentStepId, pendingFieldError, form]);

    // Handle initial cohort from navigation state
    useEffect(() => {
        if (!location.state?.initialCohort) return;

        startTransition(() => {
            handleFillingForm(location.state.initialCohort);
        });
    }, [handleFillingForm, location.state?.initialCohort]);

    // Set conversation title to form name field when messageId exists and title is available
    // (Moved from PatientSelection.tsx to centralize form population logic)
    useEffect(() => {
        if (messageId && conversationData?.conversation_title) {
            const currentName = form.getFieldValue('name');
            // Only set if name field is empty or hasn't been set yet
            if (!currentName || currentName.trim() === '') {
                form.setFieldsValue({
                    name: (conversationData?.message as any)?.content?.assistant?.content?.summary_substring,
                    description: conversationData?.conversation_title,
                });
            }
        }
    }, [messageId, conversationData?.conversation_title, conversationData?.message, form]);

    // Handle clinical trial job data from URL param (new flow)
    useEffect(() => {
        if (!clinicalTrialJobData || clinicalTrialJobData.status !== 'completed') return;

        const currentName = form.getFieldValue('name');
        // Only set if name field is empty or hasn't been set yet
        if (currentName && currentName.trim() !== '') return;

        startTransition(() => {
            const { nct_id, trial_title, executions } = clinicalTrialJobData;
            const latestExecution = executions?.[0];

            const trialName = trial_title
                ? `${nct_id} - ${trial_title.substring(0, 50)}${trial_title.length > 50 ? '...' : ''}`
                : nct_id || clinicalTrialJobData.name || 'Clinical Trial Cohort';

            form.setFieldsValue({
                name: trialName,
                description: trial_title || '',
                category_mode: 'new',
                new_category_name: 'Clinical Trials',
            });

            if (latestExecution?.final_patient_count) {
                setFilteredTotalCount(latestExecution.final_patient_count);
            }
        });
    }, [clinicalTrialJobData, form]);

    // Handle clinical trial data from navigation state (legacy modal flow)
    useEffect(() => {
        const clinicalTrialData = location.state?.clinicalTrialData;
        const patientIds = location.state?.patientIds;
        if (!clinicalTrialData && !patientIds) return;

        const { trialMetadata, executionSummary } = clinicalTrialData || {};

        startTransition(() => {
            setIsFormLoading(true);

            // Set patient IDs if passed from clinical trial
            if (patientIds && Array.isArray(patientIds) && patientIds.length > 0) {
                form.setFieldsValue({
                    patient_ids: patientIds.map(String),
                });
            }

            // Set trial metadata for name/description
            if (trialMetadata) {
                const trialName = trialMetadata?.title
                    ? `${trialMetadata.nct_id} - ${trialMetadata.title.substring(0, 50)}${trialMetadata.title.length > 50 ? '...' : ''}`
                    : trialMetadata?.nct_id || 'Clinical Trial Cohort';

                form.setFieldsValue({
                    name: trialName,
                    description: trialMetadata?.title || '',
                    category_mode: 'new',
                    new_category_name: 'Clinical Trials',
                });
            }

            if (executionSummary?.final_patient_count) {
                setFilteredTotalCount(executionSummary.final_patient_count);
            }

            setIsFormLoading(false);
        });
    }, [location.state?.clinicalTrialData, location.state?.patientIds, form]);

    return {
        // Form
        form,

        // Navigation
        currentStepId,
        setCurrentStepId,
        goNext,
        goBack,

        // Patient Data
        pagination,
        setPagination,
        paginationWithTotal,
        patientsData,
        unfilteredPatientsData,
        loadingPatients,
        filter,
        setFilter,
        patientSearchTerm,
        patientOrderBy,
        patientOrderDirection,
        handleSearch,
        handleOrderChange,

        // Form State
        errors,
        setErrors,
        clearError,
        selectedLinkedCohorts,
        setSelectedLinkedCohorts,
        excludedPatients,
        setExcludedPatients,
        filteredTotalCount,
        setFilteredTotalCount,
        handleFillingForm,
        pendingFieldError,
        setPendingFieldError,

        // UI State
        showFilter,
        setShowFilter,
        isFormLoading,
        setIsFormLoading,
        showSuccessModal,
        setShowSuccessModal,
        createdCohortName,
        setCreatedCohortName,

        // URL State
        messageId,
        clinicalTrialJobId,
        hasClinicalTrialJobId,
        clinicalTrialDescription,
        clinicalTrialJobData,
        conversationData,
    };
}

/**
 * Constate exports for CohortForm state management.
 *
 * @exports CohortFormProvider - Context provider, wrap parent component
 * @exports useCohortNavigation - Step navigation (currentStepId, goNext, goBack)
 * @exports useCohortPatientData - Patient list, pagination, filtering
 * @exports useCohortForm - Form instance and helpers
 * @exports useCohortFormErrors - Error state management
 * @exports useCohortLinkedCohorts - Linked cohort selection
 * @exports useCohortExcludedPatients - Patient exclusion state
 * @exports useCohortUIState - UI flags (loading, modals)
 * @exports useCohortContext - URL state (messageId)
 */
export const [
    /** Provider component - wrap parent to enable state access */
    CohortFormProvider,
    /** Hook for step navigation: currentStepId, setCurrentStepId, goNext, goBack */
    useCohortNavigation,
    /** Hook for patient data: pagination, patientsData, filtering, search */
    useCohortPatientData,
    /** Hook for form instance and filling helpers */
    useCohortForm,
    /** Hook for form errors management */
    useCohortFormErrors,
    /** Hook for linked cohorts state */
    useCohortLinkedCohorts,
    /** Hook for excluded patients state */
    useCohortExcludedPatients,
    /** Hook for UI state: loading, modals, filters visibility */
    useCohortUIState,
    /** Hook for URL/context state: messageId, clinicalTrialJobId */
    useCohortContext,
] = constate(
    useCohortFormState,
    // Navigation selector
    value => ({
        currentStepId: value.currentStepId,
        setCurrentStepId: value.setCurrentStepId,
        goNext: value.goNext,
        goBack: value.goBack,
    }),
    // Patient data selector
    value => ({
        pagination: value.pagination,
        setPagination: value.setPagination,
        paginationWithTotal: value.paginationWithTotal,
        patientsData: value.patientsData,
        unfilteredPatientsData: value.unfilteredPatientsData,
        loadingPatients: value.loadingPatients,
        filter: value.filter,
        setFilter: value.setFilter,
        patientSearchTerm: value.patientSearchTerm,
        patientOrderBy: value.patientOrderBy,
        patientOrderDirection: value.patientOrderDirection,
        handleSearch: value.handleSearch,
        handleOrderChange: value.handleOrderChange,
    }),
    // Form selector
    value => ({
        form: value.form,
        handleFillingForm: value.handleFillingForm,
    }),
    // Form errors selector
    value => ({
        errors: value.errors,
        setErrors: value.setErrors,
        clearError: value.clearError,
        pendingFieldError: value.pendingFieldError,
        setPendingFieldError: value.setPendingFieldError,
    }),
    // Linked cohorts selector
    value => ({
        selectedLinkedCohorts: value.selectedLinkedCohorts,
        setSelectedLinkedCohorts: value.setSelectedLinkedCohorts,
    }),
    // Excluded patients selector
    value => ({
        excludedPatients: value.excludedPatients,
        setExcludedPatients: value.setExcludedPatients,
        filteredTotalCount: value.filteredTotalCount,
        setFilteredTotalCount: value.setFilteredTotalCount,
    }),
    // UI state selector
    value => ({
        showFilter: value.showFilter,
        setShowFilter: value.setShowFilter,
        isFormLoading: value.isFormLoading,
        setIsFormLoading: value.setIsFormLoading,
        showSuccessModal: value.showSuccessModal,
        setShowSuccessModal: value.setShowSuccessModal,
        createdCohortName: value.createdCohortName,
        setCreatedCohortName: value.setCreatedCohortName,
    }),
    // Context selector (URL state and conversation)
    value => ({
        messageId: value.messageId,
        clinicalTrialJobId: value.clinicalTrialJobId,
        hasClinicalTrialJobId: value.hasClinicalTrialJobId,
        clinicalTrialDescription: value.clinicalTrialDescription,
        clinicalTrialJobData: value.clinicalTrialJobData,
        conversationData: value.conversationData,
    })
);
